import { useRef, useState } from 'react';
import {
	Box,
	BoxProps,
	Button,
	FormControl,
	IconProps,
	InputLabel,
	MenuItem,
	Popover,
	Select,
	selectClasses,
	Stack,
	SvgIconProps
} from '@mui/material';
import moment from 'moment';
import { ReactComponent as Downarrow } from '../../assets/down-arrow.svg';
import { DateRangeIcon } from 'components/CommonIcons/Icons';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTheme } from "@mui/material/styles";

type DateFilterDropdownProps = {
	filterMonth: string;
	customMonth: string;
	setFilterMonth: React.Dispatch<React.SetStateAction<string>>;
	setCustomMonth: React.Dispatch<React.SetStateAction<string>>;
	handleChange: (selectedDate: string, isCustomDate?: boolean) => void;
	boxProps?: BoxProps;
	disableFilter?: boolean;
	showTodayOption?: boolean;
	todayLabel?: string;
	isCapacity?: boolean;
	showPreviousDayOption?: boolean;
	showThisWeekOption?: boolean;
	showLastWeekOption?: boolean;
	showThisMonthOption?: boolean;
	showLastMonthOption?: boolean;
	showLast3Option?: boolean;
	showLast6Option?: boolean;
	showLast9Option?: boolean;
	showThisOption?: boolean;
	showThisLastOption?: boolean;
	showLast2Option?: boolean;
	showCustomOption?: boolean;
	isXOps?: boolean;
	isRMCost?: boolean;
	isDEQuery?: boolean;
};

const NoIcon = () => null;


const DateFilterDropdown = ({
	isCapacity,
	isDEQuery,
	filterMonth,
	customMonth,
	setFilterMonth,
	setCustomMonth,
	handleChange,
	boxProps,
	disableFilter,
	showTodayOption,
	todayLabel,
	showPreviousDayOption,
	showThisWeekOption,
	showLastWeekOption,
	showThisMonthOption,
	showLastMonthOption,
	showLast3Option,
	showLast6Option,
	showLast9Option,
	showThisOption,
	showThisLastOption,
	showLast2Option,
	showCustomOption,
	isXOps,
	isRMCost
}: DateFilterDropdownProps) => {
	const theme = useTheme();
	const colors = theme.palette;
	const { sx, ...props } = boxProps || {};
	const isCustomMonth = useRef(false);
	const [isDateOpen, setIsDateOpen] = useState(false);
	const [filterDefaultMonth, setFilterDefaultMonth] = useState('0M');
	const [customYear, setCustomYear] = useState(moment().format('YYYY'));

	const iconProps: SvgIconProps = {
		sx: {
			cursor: 'pointer',
			color: `${colors.dropdown.menuItemText} !important`,

		}
	}

	const [dateFilterMenu, setDateFilterMenu] = useState<{
		mouseX: number;
		mouseY: number;
	} | null>(null);

	const handleDateFilter = (selectedDate: string) => {
		setIsDateOpen(false);
		isCustomMonth.current = false;
		if (selectedDate === 'customMonth') {
			isCustomMonth.current = true;
			setIsDateOpen(true);
			return;
		}
		setFilterDefaultMonth(selectedDate);
		setCustomMonth('');
		setFilterMonth(selectedDate);
		handleChange(selectedDate);
	};

	const dateFilterMenuClick = (event: any) => {
		setDateFilterMenu(
			dateFilterMenu === null
				? {
					mouseX: parseInt(event.clientX) - 260,
					mouseY: parseInt(event.clientY) - 50
				}
				: null
		);
	};
	const handleDateFilterMenuClose = () => {
		setDateFilterMenu(null);
	};

	const handleMonthOnclick = (value: string) => {
		isCustomMonth.current = false;
		setIsDateOpen(false);
		setDateFilterMenu(null);

		const selectedDate = customYear + '-' + value;

		const formattedDate = moment(selectedDate, 'YYYY-MM').format('MMMM YYYY');

		setFilterMonth(formattedDate);
		setCustomMonth(selectedDate);
		handleChange(selectedDate, true);
	};
	const handleYearAdd = () => {
		let selectedYear = Number(customYear);
		selectedYear++;
		setCustomYear(selectedYear.toString());
	};
	const handleYearMinus = () => {
		let selectedYear = Number(customYear);
		selectedYear--;
		setCustomYear(selectedYear.toString());
	};
	const handleCustomOnclick = (e: any) => {
		if (e.target.textContent == 'Custom Month') {
			isCustomMonth.current = true;
			dateFilterMenuClick(e);
		}
		if (isCustomMonth.current) setIsDateOpen(true);
		else setIsDateOpen(false);

		if (e.type == 'click' && e.target.className.includes('MuiModal-backdrop')) {
			isCustomMonth.current = false;
			setDateFilterMenu(null);
			setIsDateOpen(false);
			if (customMonth == '') {
				setFilterMonth(filterDefaultMonth);
			}
		}
	};
	const months = [
		'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'
	];

	const generateXOpsMonths = () => {
		const startDate = moment('2025-03', 'YYYY-MM');
		const endDate = moment();
		const months = [];

		while (startDate.isSameOrBefore(endDate, 'month')) {
			const value = startDate.format('YYYY-MM');
			const label = startDate.format('MMMM YYYY');
			months.push({ value, label });
			startDate.add(1, 'month');
		}
		return months;
	};

	const menuItemSx = {
		...theme.typography.filterText,
		padding: '4px 8px',
		backgroundColor: `${colors.dropdown.menuItemBackground} !important`,
		color: `${colors.dropdown.menuItemText} !important`,
		'&.Mui-selected': {
			backgroundColor: `${colors.dropdown.menuItemSelected} !important`,
			color: `${colors.dropdown.menuItemHoverText} !important`,
		},
		'&.Mui-selected:hover': {
			backgroundColor: `${colors.dropdown.menuItemHover} !important`,
			color: `${colors.dropdown.menuItemHoverText} !important`,
		},
		'&:hover': {
			backgroundColor: `${colors.dropdown.menuItemHover} !important`,
			color: `${colors.dropdown.menuItemHoverText} !important`,
		},
	};

	return (
		<Box
			sx={{
				backgroundColor: isDEQuery ? '#FBFDFF' : `${colors.backgroundLight.dark}`,
				borderRadius: '4px',
				fontSize: '15px',
				zIndex: isXOps ? '' : 999,
				minWidth: '200px',
				textAlign: 'left',
				height: 'fit-content',
				...sx
			}}
			{...props}
		>
			<FormControl fullWidth>
				<Select
					value={filterMonth}
					label="Date"
					onChange={(e) => {
						handleDateFilter(e.target.value);
					}}
					IconComponent={isCapacity ? NoIcon : (isDEQuery ? DateRangeIcon : CalendarMonthIcon)}
					open={isCapacity ? false : isDateOpen}
					onClick={(e) => {
						if (!isCapacity) handleCustomOnclick(e);
					}}
					onOpen={() => {
						if (!isCapacity) setIsDateOpen(true);
					}}
					onClose={() => setIsDateOpen(false)}
					sx={{
						...theme.typography.filterText,
						width: '195px',
						paddingTop: isRMCost ? '0px' : '4px',
						[`& .${selectClasses.icon}`]: {
							transform: 'none',
							paddingTop: isRMCost ? '0px' : '2px',
						}
					}}
					MenuProps={{
						PaperProps: {
							sx: {
								backgroundColor: `${colors.dropdown.menuItemBackground} !important`,
								borderRadius: '4px',
							},
						},
						MenuListProps: {
							sx: {
								paddingTop: 0,
								paddingBottom: 0,
							},
						},
					}}
					disabled={disableFilter}
				>
					{showTodayOption && <MenuItem value={'0T'} sx={menuItemSx}>{todayLabel || 'Last 24 Hours'}</MenuItem>}
					{showPreviousDayOption && <MenuItem value={'1T'} sx={menuItemSx}>Previous Day</MenuItem>}
					{showThisWeekOption && <MenuItem value={'0W'} sx={menuItemSx}>This Week</MenuItem>}
					{showLastWeekOption && <MenuItem value={'1W'} sx={menuItemSx}>Last Week</MenuItem>}
					{/* {!isXOps && <MenuItem value={'0M'}>This Month</MenuItem>}
					<MenuItem value={isXOps ?'2025-03':'1M'}>{isXOps ? 'March 2025' : 'Last Month'}</MenuItem>
					<MenuItem value={isXOps ?'2025-02':'3M'}>{isXOps ? 'February 2025' :'Last 3 Months'}</MenuItem> */}
					{!isXOps && <MenuItem value={'0M'} sx={menuItemSx}>This Month</MenuItem>}
					{isXOps ? (
						generateXOpsMonths().map(({ value, label }) => (
							<MenuItem key={value} value={value}
								selected={filterMonth === value}
								sx={{
									...menuItemSx,
									'&.Mui-selected': {
										backgroundColor: '#005092 !important',
										color: '#fff !important',
									},
									// '&:hover': {
									//   backgroundColor: '#005092',
									// },
								}}
							>
								{label}
							</MenuItem>
						))
					) : (
						<>
						</>
					)}
					{!isXOps && <MenuItem value={'1M'} sx={menuItemSx}>Last Month</MenuItem>}
					{!isXOps && <MenuItem value={'3M'} sx={menuItemSx}>Last 3 Months</MenuItem>}
					{!isXOps && <MenuItem value={'6M'} sx={menuItemSx}>Last 6 Months</MenuItem>}
					{!isXOps && <MenuItem value={'9M'} sx={menuItemSx}>Last 9 Months</MenuItem>}
					{!isXOps && <MenuItem value={'0Y'} sx={menuItemSx}>This Year</MenuItem>}
					{!isXOps && <MenuItem value={'1Y'} sx={menuItemSx}>Last Year</MenuItem>}
					{!isXOps && <MenuItem value={'2Y'} sx={menuItemSx}>Last 2 Years</MenuItem>}
					{!isXOps && isRMCost && <MenuItem value={'3Y'} sx={menuItemSx}>Last 3 Years</MenuItem>}
					{!isXOps && <MenuItem value={'customMonth'} sx={menuItemSx}>Custom Month</MenuItem>}
					{months.some((month) => filterMonth.includes(month)) && (
						<MenuItem disabled value={filterMonth} sx={menuItemSx}>
							{filterMonth}
						</MenuItem>
					)}
				</Select>
			</FormControl>
			{isCustomMonth.current && (
				<Popover
					open={dateFilterMenu !== null}
					onClose={handleDateFilterMenuClose}
					anchorReference="anchorPosition"
					anchorPosition={
						dateFilterMenu !== null ? { top: dateFilterMenu.mouseY, left: dateFilterMenu.mouseX } : undefined
					}
				>
					<Box
						sx={{
							backgroundColor: `${colors.dropdown.menuItemBackground} !important`,
							borderRadius: '4px',
							width: '175px',
							minHeight: '30px'
						}}
					>
						<Stack p={1} direction="row" alignItems="center" justifyContent="space-between">
							<InputLabel sx={{
								...theme.typography.filterText,
								color: `${colors.dropdown.menuItemText} !important`,
								fontWeight: 500,
								'&.MuiFormLabel-root': { color: `${colors.dropdown.menuItemText} !important` },
								'&.Mui-focused': { color: `${colors.dropdown.menuItemText} !important` },
								'&.Mui-disabled': { color: `${colors.dropdown.menuItemText} !important` },
								'&.MuiInputLabel-root': { color: `${colors.dropdown.menuItemText} !important` },
							}}>{customYear}</InputLabel>
							<Stack direction="row" gap={0.5}>
								<KeyboardArrowUpIcon {...iconProps} onClick={handleYearAdd} />
								<KeyboardArrowDownIcon {...iconProps} onClick={handleYearMinus} />
							</Stack>
						</Stack>
						<div className="custom-dis">
							<Button variant="outlined" className="custom-month-btn" onClick={() => handleMonthOnclick('01')}>
								January
							</Button>
							<Button variant="outlined" className="custom-month-btn" onClick={() => handleMonthOnclick('02')}>
								February
							</Button>
						</div>
						<div className="custom-dis">
							<Button variant="outlined" className="custom-month-btn" onClick={() => handleMonthOnclick('03')}>
								March
							</Button>
							<Button variant="outlined" className="custom-month-btn" onClick={() => handleMonthOnclick('04')}>
								April
							</Button>
						</div>
						<div className="custom-dis">
							<Button variant="outlined" className="custom-month-btn" onClick={() => handleMonthOnclick('05')}>
								May
							</Button>
							<Button variant="outlined" className="custom-month-btn" onClick={() => handleMonthOnclick('06')}>
								Jun
							</Button>
						</div>
						<div className="custom-dis">
							<Button variant="outlined" className="custom-month-btn" onClick={() => handleMonthOnclick('07')}>
								July
							</Button>
							<Button variant="outlined" className="custom-month-btn" onClick={() => handleMonthOnclick('08')}>
								August
							</Button>
						</div>
						<div className="custom-dis">
							<Button variant="outlined" className="custom-month-btn" onClick={() => handleMonthOnclick('09')}>
								September
							</Button>
							<Button variant="outlined" className="custom-month-btn" onClick={() => handleMonthOnclick('10')}>
								October
							</Button>
						</div>
						<div className="custom-dis">
							<Button variant="outlined" className="custom-month-btn" onClick={() => handleMonthOnclick('11')}>
								November
							</Button>
							<Button variant="outlined" className="custom-month-btn" onClick={() => handleMonthOnclick('12')}>
								December
							</Button>
						</div>
					</Box>
				</Popover>
			)}
		</Box>
	);
};

export default DateFilterDropdown;
